import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EventsState } from '../../../../../store/types';
import { GetDefaultSlideshow } from '../../../../../store/utils/create-event';
import {
	PageModule,
	SlideshowContent,
} from '../../../../../types/working-model';
import Icon, { ICONS, COLORS } from '../../../../general-ui/icon';
import Slider from 'react-slick';
import LargeButton from '../../../../general-ui/button/large-button';
import ModalComponent from '../../../../general-ui/modal/modal';
import { updatePageModule } from '../../../../../store/actions/admin/create-event/session';
import { UploadFile } from '../../../../../connection/uploads';
import WaitingIndicator from '../../../../general-ui/waiting-indicator/waiting-indicator';
import SelectInput from '../../../../general-ui/select/select';
import Switch from '../../../../general-ui/switch/switch';
import EditableCustomTitle from '../../../../general-ui/editable-title/editable-custom-title';
import './slideshows.scss';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function PrevArrow(props: any): JSX.Element {
	const { className, style, onClick } = props;

	return (
		<div onClick={onClick} className={className} style={{ ...style }}>
			<Icon name={ICONS.ARROW_LEFT} size={20} color={COLORS.WHITE} />
		</div>
	);
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function NextArrow(props: any): JSX.Element {
	const { className, style, onClick } = props;

	return (
		<div onClick={onClick} className={className} style={{ ...style }}>
			<Icon name={ICONS.ARROW_RIGHT} size={20} color={COLORS.WHITE} />
		</div>
	);
}
interface Props {
	page_module: PageModule;
}

export default function Slideshows(props: Props): JSX.Element | null {
	const { page_module } = props;
	const {
		CreateSessionReducer: { workingSession },
		AuthReducer: { token, user },
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();

	const [modalOpen, setModalOpen] = useState(false);
	const [uploading, setUploading] = useState(false);
	const imageRef = useRef<HTMLInputElement | null>(null);
	const content: SlideshowContent =
		page_module.content ?? GetDefaultSlideshow();
	const [workingSlideshow, setWorkingSlideshow] = useState(content);

	const settings = {
		dots: true,
		infinite: true,
		speed: 500,
		slidesToShow: 1,
		slidesToScroll: 1,
	};

	function openModal() {
		setModalOpen(true);
	}

	function closeModal() {
		setModalOpen(false);
	}

	function cancel() {
		setWorkingSlideshow(content);
		setModalOpen(false);
	}

	function finished() {
		dispatch(
			updatePageModule({
				...page_module,
				content: workingSlideshow,
			})
		);
		setModalOpen(false);
	}

	function uploadNewImage() {
		imageRef.current?.click();
	}

	async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
		const files = e.target.files;

		if (files) {
			setUploading(true);
			const uploaded: string[] = [];

			for (let i = 0; i < files.length; ++i) {
				uploaded[i] = await UploadFile(user!, token!, files[i]);
			}

			setWorkingSlideshow((slideshow: SlideshowContent) => {
				return {
					...slideshow,
					images: [...slideshow.images, ...uploaded],
				};
			});

			setUploading(false);
		}
	}

	function removeImage(index: number) {
		return () => {
			setWorkingSlideshow((slideshow) => {
				return {
					...slideshow,
					images: slideshow.images.filter(
						(img: string, _index: number) => _index !== index
					),
				};
			});
		};
	}

	function updateDuration(value: number) {
		setWorkingSlideshow((slideshow) => ({ ...slideshow, duration: value }));
	}

	function setAdvanceByModerator(value: string, on: boolean) {
		setWorkingSlideshow((slideshow) => ({
			...slideshow,
			advance_by_moderator: on,
		}));
	}

	async function handleFiles(files: FileList | File) {
		setUploading(true);
		if (files instanceof FileList) {
			const uploaded: string[] = [];
			for (let i = 0; i < files.length; ++i) {
				uploaded[i] = await UploadFile(user!, token!, files[i]);
			}

			setWorkingSlideshow((slideshow) => ({
				...slideshow,
				images: [...slideshow.images, ...uploaded],
			}));
		} else if (files instanceof File) {
			const uploaded = await UploadFile(user!, token!, files);
			setWorkingSlideshow((slideshow) => ({
				...slideshow,
				images: [...slideshow.images, uploaded],
			}));
		}
		setUploading(false);
	}

	return page_module.is_on && (!workingSession?.layout?.withMenu || page_module) ? (
		<div className="editor-wrapper sessions slideshow">
			<div className="container">
				<EditableCustomTitle
					moduleTitle="Slideshow"
					element="h5"
					content={page_module?.content || null}
					pageModule={page_module}
				/>
				{content?.images?.length > 0 ? (
					<div className="slideshow-images-slider">
						<Slider
							prevArrow={<PrevArrow />}
							nextArrow={<NextArrow />}
							{...settings}
						>
							{content.images.map(
								(image: string, index: number) => (
									<div
										className="slideshow-image-slide"
										key={index}
									>
										<img src={image} alt={image} />
									</div>
								)
							)}
						</Slider>
						<div className="slideshow-images-actions">
							<button className="round" onClick={openModal}>
								<Icon
									name={ICONS.EDIT}
									size={14}
									color={COLORS.WHITE}
								/>
							</button>
						</div>
					</div>
				) : (
					<LargeButton title={'Add Images'} onClick={openModal} />
				)}
			</div>
			<ModalComponent
				closeable={false}
				cancellable={false}
				open={modalOpen}
				onRequestClose={closeModal}
				title={'Edit Slideshow'}
				footer={
					<>
						<button onClick={cancel} style={{ marginRight: 16 }}>
							Cancel
						</button>
						<button onClick={finished} className="primary">
							Done
						</button>
					</>
				}
			>
				<>
					{workingSlideshow?.images?.length > 0 ? (
						<div className="slideshow-images">
							{workingSlideshow.images.map(
								(image: string, index: number) => (
									<div
										className="slideshow-image"
										key={index}
										style={{
											backgroundImage: `url(${image.replace(
												/ /g,
												'%20'
											)})`,
										}}
									>
										<div className="slideshow-image-actions">
											{/* <button onClick={() => { }} className="no-style"><Icon name={ICONS.EDIT} size={14} color={COLORS.WHITE} /></button> */}
											<button
												className="no-style"
												onClick={removeImage(index)}
											>
												<Icon
													name={ICONS.TRASH}
													size={14}
													color={COLORS.WHITE}
												/>
											</button>
										</div>
									</div>
								)
							)}
							{uploading && (
								<div className="slideshow-image">
									<WaitingIndicator />
								</div>
							)}
							{!uploading && (
								<button
									className="add-image"
									onClick={uploadNewImage}
								>
									<Icon
										name={ICONS.ADD}
										size={14}
										color={COLORS.WHITE}
									/>{' '}
									Upload Images
								</button>
							)}
							<input
								type="file"
								multiple={true}
								style={{ display: 'none' }}
								onChange={handleImageUpload}
								accept=".jpg,.jpeg,.png,.gif"
								ref={imageRef}
							/>
						</div>
					) : (
						<LargeButton
							allowedFileTypes={[
								'image/jpeg',
								'image/png',
								'image/gif',
							]}
							onFile={handleFiles}
							title="Upload Images"
							subtitle="Allowed file types are jpeg, png, and gif"
							multiple={true}
							uploading={uploading}
						/>
					)}

					<SelectInput
						options={[
							{
								value: 5,
								label: '5 Seconds',
							},
							{
								value: 10,
								label: '10 Seconds',
							},
							{
								value: 30,
								label: '30 Seconds',
							},
							{
								value: -1,
								label: 'Manual change only',
							},
						]}
						selected={workingSlideshow.duration}
						onChange={updateDuration}
						label={'Time Per Slide'}
					/>

					<div className="field-group switch">
						<Switch
							on={workingSlideshow.advance_by_moderator}
							onClick={setAdvanceByModerator}
							value={'advance-by-moderator'}
						/>
						<label>Only moderator can change slides</label>
					</div>
				</>
			</ModalComponent>
		</div>
	) : null;
}
